import React, { useState, useEffect } from 'react';
import { Card, Table, Alert, Spinner } from 'react-bootstrap';
import { stockItemAPI } from '../api/stockItemAPI';
import { formatQuantity } from '../utils/commonUtils';

const CostHistoryDisplay = ({ stockItemId }) => {
    const [costHistory, setCostHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;

        const fetchCostHistory = async () => {
            if (!stockItemId) return;

            setLoading(true);
            setError(null);

            try {
                const data = await stockItemAPI.getCostHistory(stockItemId);
                if (!isMounted) return;
                setCostHistory(data || []);
            } catch (err) {
                if (!isMounted) return;
                console.error('Error fetching cost history:', err);
                setError('Failed to load cost history');
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchCostHistory();

        return () => {
            isMounted = false;
        };
    }, [stockItemId]);

    // Format date to a readable format
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return 'N/A';
        return `$${parseFloat(amount).toFixed(2)}`;
    };

    if (!stockItemId) {
        return null;
    }

    if (loading) {
        return (
            <Card className="mb-3">
                <Card.Body>
                    <Card.Title style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                        Historical Cost Data
                    </Card.Title>
                    <div className="text-center py-3">
                        <Spinner animation="border" size="sm" />
                        <div className="mt-2 text-muted" style={{ fontSize: '0.875rem' }}>Loading cost history...</div>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="mb-3">
                <Card.Body>
                    <Card.Title style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                        Historical Cost Data
                    </Card.Title>
                    <Alert variant="warning" style={{ fontSize: '0.875rem', marginBottom: 0 }}>
                        {error}
                    </Alert>
                </Card.Body>
            </Card>
        );
    }

    if (costHistory.length === 0) {
        return (
            <Card className="mb-3">
                <Card.Body>
                    <Card.Title style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                        Historical Cost Data
                    </Card.Title>
                    <Alert variant="info" style={{ fontSize: '0.875rem', marginBottom: 0 }}>
                        No cost history available for this item yet.
                    </Alert>
                </Card.Body>
            </Card>
        );
    }

    return (
        <Card className="mb-3">
            <Card.Body>
                <Card.Title style={{ fontSize: '1rem', marginBottom: '1rem' }}>
                    Historical Cost Data
                    <span className="text-muted ms-2" style={{ fontSize: '0.875rem', fontWeight: 'normal' }}>
                        (Last {costHistory.length} receive{costHistory.length !== 1 ? 's' : ''})
                    </span>
                </Card.Title>
                <div style={{ overflowX: 'auto' }}>
                    <Table bordered hover size="sm" style={{ fontSize: '0.875rem', marginBottom: 0 }}>
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '120px' }}>Date</th>
                                <th style={{ width: '120px' }}>Unit Cost</th>
                                <th style={{ width: '100px' }}>Quantity</th>
                                <th>Vendor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {costHistory.map((record, index) => (
                                <tr key={index}>
                                    <td>{formatDate(record.received_date)}</td>
                                    <td className="fw-bold">{formatCurrency(record.cost_per_unit)}</td>
                                    <td>{formatQuantity(record.quantity)}</td>
                                    <td>{record.vendor_name || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
        </Card>
    );
};

export default CostHistoryDisplay;
